import type { BlockFootprint, OverhangPiece } from '@ranked-waiting/stack-tower-core';

/**
 * Falling-debris physics (spec §Phase 4). Deliberately *simple explicit
 * integration* — gravity + a constant spin, no real physics engine, no
 * collision — because the pieces are pure cosmetic feedback for "that part
 * got cut off", never interact with each other or the tower, and are alive
 * for well under a second before leaving the screen. The pure
 * `@ranked-waiting/stack-tower-core` package stays free of any of this: a
 * falling piece never feeds back into scored state, so it belongs entirely
 * in the rendering layer.
 *
 * No object pool for v1 — realistically 1-2 pieces are alive at once (one
 * overhang per placement, gone in ~0.5s); pooling is a "revisit only if
 * profiling shows GC pressure" item, not a day-one need, the same call the
 * plan already made.
 *
 * World-space convention matches the rest of the render layer: `cx`/`cy` are
 * world coordinates with **Y increasing upward** (0 = ground), so gravity
 * *subtracts* from `vy`. The render layer maps world→screen (which flips Y).
 */
export interface FallingPiece {
  /** World-space centre. `cy` is up-positive, like every other worldY in this feature. */
  cx: number;
  cy: number;
  halfWidth: number;
  halfHeight: number;
  /** Velocity in world units/sec. `vy` up-positive; gravity pulls it negative. */
  vx: number;
  vy: number;
  /** Radians; purely visual tumble. */
  rotation: number;
  angularVel: number;
  color: string;
}

/** Downward acceleration, world units/sec². Tuned so a piece clears a ~512-unit viewport in roughly half a second — long enough to read, short enough not to litter. */
export const FALL_GRAVITY_UNITS_PER_SEC2 = 4200;

const OVERHANG_PUSH_UNITS_PER_SEC = 220; // sideways kick, away from the tower
const OVERHANG_POP_UNITS_PER_SEC = 90; // small upward hop so it arcs rather than just dropping
const OVERHANG_SPIN_RAD_PER_SEC = 6;
const MISS_POP_UNITS_PER_SEC = 40;
const MISS_SPIN_RAD_PER_SEC = 3;

function centreFrom(footprint: BlockFootprint, worldYBottom: number, blockHeight: number) {
  return {
    cx: (footprint.min + footprint.max) / 2,
    cy: worldYBottom + blockHeight / 2,
    halfWidth: (footprint.max - footprint.min) / 2,
    halfHeight: blockHeight / 2,
  };
}

/** How much harder a near-miss overhang launches (spec §12: "exaggerate the falling overhang... allow a slightly longer physics fall") — bigger kick and spin, same gravity, so it visibly takes longer to clear the screen. */
const NEAR_MISS_LAUNCH_MULTIPLIER = 1.8;

/**
 * The sliver cut off on a non-perfect hit, launched away from the tower on
 * the side it overhung. `worldYBottom` is the bottom edge of the block that
 * was just placed (the sliver shares that row). `dramatic` exaggerates the
 * launch for a near-miss (spec §12) — a normal cut's overhang is unchanged.
 */
export function spawnOverhangPiece(
  overhang: OverhangPiece,
  worldYBottom: number,
  blockHeight: number,
  color: string,
  dramatic = false,
): FallingPiece {
  const dir = overhang.side === 'right' ? 1 : -1;
  const mult = dramatic ? NEAR_MISS_LAUNCH_MULTIPLIER : 1;
  return {
    ...centreFrom(overhang, worldYBottom, blockHeight),
    vx: dir * OVERHANG_PUSH_UNITS_PER_SEC * mult,
    vy: OVERHANG_POP_UNITS_PER_SEC * mult,
    rotation: 0,
    angularVel: dir * OVERHANG_SPIN_RAD_PER_SEC * mult,
    color,
  };
}

/**
 * The whole moving block on a miss — it toppled off the tower entirely.
 * `direction` is which way it was overhanging when it missed (sign of the
 * offset from the previous centre), so it tumbles off that side.
 */
export function spawnMissPiece(
  footprint: BlockFootprint,
  worldYBottom: number,
  blockHeight: number,
  direction: number,
  color: string,
): FallingPiece {
  const dir = direction >= 0 ? 1 : -1;
  return {
    ...centreFrom(footprint, worldYBottom, blockHeight),
    vx: dir * OVERHANG_PUSH_UNITS_PER_SEC * 0.7,
    vy: MISS_POP_UNITS_PER_SEC,
    rotation: 0,
    angularVel: dir * MISS_SPIN_RAD_PER_SEC,
    color,
  };
}

/** Advances one piece in place by `dtMs`. Mutation is intentional — the engine owns the array and this is hot per-frame code. */
export function stepFallingPiece(piece: FallingPiece, dtMs: number): void {
  const dt = dtMs / 1000;
  piece.vy -= FALL_GRAVITY_UNITS_PER_SEC2 * dt;
  piece.cx += piece.vx * dt;
  piece.cy += piece.vy * dt;
  piece.rotation += piece.angularVel * dt;
}
